import { isEmpty, omit, isNull, isNil, isUndefined } from 'lodash';

import { ISavedFilter } from '../../../../../components/sidebar-filters/saved-filter/interface';
import { ISidebarFilterOptionsContext } from '../../../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { TVendorBillItemFilterKey, VendorBillItemFilterKeys } from './VendorBillItemSidebarFilters';
import { DEFAULT_DATE_FORMAT, getUtcFormattedTime, parseDateFromString } from '../../../../../domain/date/date';
import { IDateRange } from '../../../../interface';

export const transformVendorBillItemsSavedFilterToUser = (
    filter: ISavedFilter['filter_values'],
    context: ISidebarFilterOptionsContext
): Record<TVendorBillItemFilterKey, string> => {
    let result: Record<TVendorBillItemFilterKey, string> = {} as Record<TVendorBillItemFilterKey, string>;

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid displaying them as "unknown"
        if (isUndefined(value) || isNull(value)) continue;

        switch (key) {
            case VendorBillItemFilterKeys.period:
                // for custom range value we use such structure 'key::period'
                const clearPeriodValue = parseClearPeriodValue(value);

                const period =
                    context.vendorBillItemPeriodOptions.find(option => {
                        return option.value === clearPeriodValue;
                    })?.label || 'unknown';

                if (clearPeriodValue !== 'custom_range') {
                    result[key] = period;
                    break;
                }

                const dateRange = parseDateRangeValue(value);

                if (!dateRange) {
                    result[key] = 'unknown';
                    break;
                }

                result[key] =
                    `${period}(${getUtcFormattedTime(dateRange.start, DEFAULT_DATE_FORMAT)}-${getUtcFormattedTime(dateRange.end, DEFAULT_DATE_FORMAT)})`;
                break;
            case VendorBillItemFilterKeys.markets:
                result[key] = value
                    .map((val: string) => {
                        const option = context.marketOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case VendorBillItemFilterKeys.programs:
                result[key] = value
                    .map((val: string) => {
                        const option = context.vendorBillItemProgramOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case VendorBillItemFilterKeys.utilities:
                result[key] = value
                    .map((val: string) => {
                        const option = context.vendorBillItemUtilityOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case VendorBillItemFilterKeys.transactionIds:
                result[key] = value
                    .map((val: string) => {
                        const option = context.vendorBillTransactionIdOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case VendorBillItemFilterKeys.event:
                result[key] = context.hasEventOptions.find(option => option.value === value)?.label || 'unknown';
                break;
            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    // Remove empty properties from the result object
    const emptyProperties = getEmptyFilterProperties(result);
    if (emptyProperties.length > 0) {
        result = omit(result, emptyProperties) as Record<TVendorBillItemFilterKey, string>;
    }

    return result;
};

export const transformVendorBillItemsSavedFilterValuesFromDto = (
    filter: ISavedFilter['filter_values']
): Record<TVendorBillItemFilterKey, unknown> => {
    const result: Record<TVendorBillItemFilterKey, unknown> = {} as Record<TVendorBillItemFilterKey, unknown>;

    // Convert values from DTO format to interface format
    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value)) continue;

        switch (key) {
            case VendorBillItemFilterKeys.period:
            case VendorBillItemFilterKeys.markets:
            case VendorBillItemFilterKeys.programs:
            case VendorBillItemFilterKeys.utilities:
            case VendorBillItemFilterKeys.transactionIds:
            case VendorBillItemFilterKeys.event:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = value; // Keep other values as is
        }
    }

    return result;
};

export const transformVendorBillItemsSavedFilterValuesToDto = (filter: ISavedFilter['filter_values']): Record<TVendorBillItemFilterKey, unknown> => {
    const result: Record<TVendorBillItemFilterKey, unknown> = {} as Record<TVendorBillItemFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        switch (key) {
            case VendorBillItemFilterKeys.period:
            case VendorBillItemFilterKeys.markets:
            case VendorBillItemFilterKeys.programs:
            case VendorBillItemFilterKeys.utilities:
            case VendorBillItemFilterKeys.transactionIds:
            case VendorBillItemFilterKeys.event:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    return result;
};

export const checkFilterInvalidValues = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    const invalidFields: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        if (isNil(value) || isEmpty(value)) continue;

        switch (key) {
            case VendorBillItemFilterKeys.period:
                if (
                    typeof value === 'string' &&
                    !context.vendorBillItemPeriodOptions.some(opt => {
                        // for custom range value we use such structure 'key::period'
                        const clearPeriod = value?.split('::')?.at(0);

                        return opt.value === clearPeriod;
                    })
                ) {
                    invalidFields.push(key);
                }
                break;
            case VendorBillItemFilterKeys.markets:
                if (Array.isArray(value)) {
                    const validOptionValues = context.marketOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case VendorBillItemFilterKeys.programs:
                if (Array.isArray(value)) {
                    const validOptionValues = context.vendorBillItemProgramOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case VendorBillItemFilterKeys.utilities:
                if (Array.isArray(value)) {
                    const validOptionValues = context.vendorBillItemUtilityOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case VendorBillItemFilterKeys.transactionIds:
                if (Array.isArray(value)) {
                    const validOptionValues = context.vendorBillTransactionIdOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case VendorBillItemFilterKeys.event:
                if (typeof value === 'string' && !context.hasEventOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;
        }
    }

    return invalidFields;
};

export function countNotEmptyFilters(filters: Record<string, any>): number {
    if (!filters) return 0;

    return Object.values(filters).filter(value => {
        if (Array.isArray(value)) return value.length > 0;

        if (typeof value === 'number') return !isNaN(value);

        if (typeof value === 'string') return value.trim() !== '';

        if (typeof value === 'boolean') return true;

        return false;
    }).length;
}

function getEmptyFilterProperties(obj: Record<TVendorBillItemFilterKey, string>): TVendorBillItemFilterKey[] {
    return Object.entries(obj)
        .filter(([_, value]) => isEmpty(value))
        .map(([key, _]) => key) as TVendorBillItemFilterKey[];
}

export function parseClearPeriodValue(value: string | null | undefined): string | null {
    // for custom range value we use such structure 'key::period'
    return value?.split('::')?.at(0) || null;
}

export function parseDateRangeValue(value: string | null | undefined): IDateRange | null {
    if (!value?.includes('custom_range')) {
        return null;
    }

    // for custom range value we use such structure 'key::period'
    const dateRangeString = value?.split('::')?.reverse()?.at(0);

    if (!dateRangeString) {
        return null;
    }

    const [start, end] = dateRangeString.split('_');

    return { start: parseDateFromString(start), end: parseDateFromString(end) };
}
